import { Context as DiceWorkerContext } from "@/context";
import type { User } from "@dndbeyond/auth/types/user";
import { isEmpty } from "lodash";
import {
  useEffect,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";
import { DiceCanvases } from "./DiceCanvases";
import { getPhysicsWorker } from "./helpers/getPhysicsWorker";
import { getRenderedWorker } from "./helpers/getRenderedWorker";
import { useManifests } from "./hooks/useManifests";

interface PddContextProviderProps extends PropsWithChildren {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- logger type needs to be properly decoupled from feywild
  logger: any;
  errorCallback?: (message: string) => void;
  nodeEnv: string;
  env: string;
  user?: User;
  gameId?: string;
  stt?: string;
  setId?: string;
  toastCallback?: (message: string) => void;
  canUseShared3dDice?: boolean;
}

export const PddContextProvider: FC<PddContextProviderProps> = ({
  children,
  logger,
  errorCallback,
  nodeEnv,
  env,
  user,
  gameId,
  stt,
  setId,
  toastCallback,
  canUseShared3dDice,
}) => {
  const hasWorkersInitialized = useRef(false);
  const manifests = useManifests(nodeEnv, env, gameId, stt, setId);

  const [physicsWorker, setPhysicsWorker] = useState<Worker>();
  const [hasPhysicsWorkerErrored, setHasPhysicsWorkerErrored] = useState(false);
  const [isPhysicsWorkerReady, setIsPhysicsWorkerReady] = useState(false);

  const [renderedWorker, setRenderedWorker] = useState<Worker>();
  const [hasRenderedWorkerErrored, setHasRenderedWorkerErrored] =
    useState(false);
  const [isRenderedWorkerReady, setIsRenderedWorkerReady] = useState(false);

  const hasPddErrored = hasPhysicsWorkerErrored || hasRenderedWorkerErrored;
  const isPddReady = isPhysicsWorkerReady && isRenderedWorkerReady;

  useEffect(() => {
    if (
      !canUseShared3dDice ||
      !user ||
      isEmpty(manifests) ||
      hasWorkersInitialized.current
    )
      return;

    (async () => {
      setPhysicsWorker(await getPhysicsWorker(nodeEnv, env, user));
      setRenderedWorker(await getRenderedWorker(nodeEnv, env, user));

      hasWorkersInitialized.current = true;
    })();
  }, [canUseShared3dDice, env, manifests, nodeEnv, user]);

  // pass-through children if no user is present
  // e.g. readonly character sheets (can't roll dice)
  if (!(canUseShared3dDice && user)) return children;

  return (
    <DiceWorkerContext.Provider
      value={{
        renderedWorker,
        physicsWorker,
        nodeEnv,
        env,
        user,
        hasPddErrored,
        isPddReady,
        manifests,
        setHasPhysicsWorkerErrored,
        setIsPhysicsWorkerReady,
        gameId,
        stt,
        setId,
        logger,
        errorCallback,
        toastCallback,
        setHasRenderedWorkerErrored,
        setIsRenderedWorkerReady,
      }}
    >
      {children}
      <DiceCanvases />
    </DiceWorkerContext.Provider>
  );
};
